import { useCallback, useEffect, useRef } from 'react';

/**
 * Manages one or more AbortControllers tied to the component lifecycle.
 *
 * - getSignal(lane?) - returns the current signal for the lane. Lazily creates
 *   a controller if there is none, and re-creates it if the previous one was
 *   already aborted (so retries after a cancelled request still work).
 * - nextSignal(lane?) - aborts the existing controller for the lane and
 *   creates a fresh one. Use this when a new user action should cancel any
 *   in-flight request for the same purpose (filters, search, repeated submit).
 * - abort(lane?) - aborts a single lane (when provided) or all lanes.
 *
 * On unmount every controller created via this hook is aborted automatically,
 * so requests started during render do not keep running after the user leaves
 * the page.
 *
 * @returns {{
 *   getSignal: (lane?: string) => AbortSignal,
 *   nextSignal: (lane?: string) => AbortSignal,
 *   abort: (lane?: string) => void,
 * }}
 */
export function useAbortController() {
    const controllersRef = useRef(null);
    if (controllersRef.current === null) {
        controllersRef.current = new Map();
    }

    useEffect(() => {
        const controllers = controllersRef.current;
        return () => {
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, []);

    const getSignal = useCallback((lane = 'default') => {
        const controllers = controllersRef.current;
        let controller = controllers.get(lane);
        if (!controller || controller.signal.aborted) {
            controller = new AbortController();
            controllers.set(lane, controller);
        }
        return controller.signal;
    }, []);

    const nextSignal = useCallback((lane = 'default') => {
        const controllers = controllersRef.current;
        const existing = controllers.get(lane);
        if (existing) {
            existing.abort();
        }
        const controller = new AbortController();
        controllers.set(lane, controller);
        return controller.signal;
    }, []);

    const abort = useCallback((lane) => {
        const controllers = controllersRef.current;
        if (lane === undefined) {
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
            return;
        }
        const existing = controllers.get(lane);
        if (existing) {
            existing.abort();
            controllers.delete(lane);
        }
    }, []);

    return { getSignal, nextSignal, abort };
}
